/**
 *
 * Settings
 *
 */

import React, { memo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { Link } from "react-router-dom";
import { saveAs } from "file-saver";
import { useTranslation} from "react-i18next";

// styles
import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import { flushStateSettings, loadSettings } from "./actions";

import {
  selectSettingsList,
  selectSettingsLoading,
  selectSettingsError,
} from "./selectors";

// antd component
import { Button, Layout } from "antd";
import { Content } from "antd/es/layout/layout";

// components
import CustomPageHeader from "components/CustomPageHeader";
import CustomTable from "components/CustomTable";

// utils
import api from "utils/api";
import { slugify } from "utils/helpers/helper.general";

export function Settings({
  // main data
  settingsList,
  // states
  error,
  loading,
  // actions
  loadSettings,
  flushState,
}) {
  useInjectReducer({ key: "settings", reducer });
  useInjectSaga({ key: "settings", saga });

  const { t } = useTranslation();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // --> main code here
    !settingsList && loadSettings();

    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state after unmounting a component
      flushState();
    };
  }, []);

  // TODO  FIX LAYOUT FOR TABLE
  const columns = [
    {
      title: t("common.lastNameShort"),
      dataIndex: "lastname",
      key: "column-lastname",
      className: "column-lastname",
      sorter: (a, b) =>
        a?.description?.lastname.localeCompare(b?.description?.lastname),
      render: (text, record) => {
        return {
          children: (
            <Link className="link-table" to={`/etudiants/${record._id}`}>
              {record?.description?.lastname}
            </Link>
          ),
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("common.firstName"),
      dataIndex: "firstname",
      key: "column-firstname",
      className: "column-firstname",
      sorter: (a, b) =>
        a?.description?.firstname.localeCompare(b?.description?.firstname),
      render: (text, record, index) => {
        return (
          <Link className="link-table" to={`/etudiants/${record._id}`}>
            {record?.description?.firstname}
          </Link>
        );
      },
    },
    // {
    //   title: "Date de naissance",
    //   dataIndex: "birthdate",
    //   key: "column-birthdate",
    //   className: "column-birthdate",
    //   render: (text, record, index) => {
    //     return (
    //       <Link className="link-table" to={`/etudiants/${record?._id}`}>
    //         {convertDateToString(record?.birthdate)}
    //       </Link>
    //     );
    //   },
    // },
    // {
    //   title: "Sexe",
    //   dataIndex: "sex",
    //   key: "column-sex",
    //   className: "column-sex",
    //   render: (text, record, index) => {
    //     return (
    //       <Link className="link-table" to={`/etudiants/${record._id}`}>
    //         {record?.sex}
    //       </Link>
    //     );
    //   },
    // },
    {
      title: t("labels.class"),
      dataIndex: "class_info",
      key: "column-class_info",
      className: "column-class_info",
      sorter: (a, b) =>
        a?.class_info?.title.localeCompare(b?.class_info?.title),
      render: (text, record, index) => {
        return (
          <Link
            className="link-table"
            to={`/classes/${record.class_info?._id}`}
          >
            {record?.class_info?.title}
          </Link>
        );
      },
    },
    {
      title: "N° ID ISM",
      dataIndex: "ismTD",
      key: "column-ismTD",
      className: "column-ismTD",
      sorter: (a, b) => a?.ismTD.localeCompare(b?.ismTD),
      render: (text, record, index) => record?.ismTD,
    },
    {
      title: "N° AVS",
      dataIndex: "avsNumber",
      key: "column-avsNumber",
      className: "column-avsNumber",
      sorter: (a, b) => a?.avsNumber.localeCompare(b?.avsNumber),
      render: (text, record, index) => record?.avsNumber,
    },
    {
      title: t("labels.parents"),
      dataIndex: "parents",
      key: "column-parents",
      className: "column-parents",
      sorter: (a, b) =>
        a?.parents?.length
          ?.toString()
          .localeCompare(b?.parents?.length?.toString()),
      render: (text, record, index) => {
        return record?.parents?.length;
      },
    },
    // {
    //   title: "Adresse",
    //   dataIndex: "address",
    //   key: "column-address",
    //   className: "column-address",
    //   render: (text, record, index) => {
    //     return (
    //       !!record?.address &&
    //       `${record?.address?.city} ${record?.address?.zip} ${
    //         record?.address?.street
    //       }`
    //     );
    //   },
    // },
  ];

  const onSelectChange = newSelectedRowKeys => {
    setSelectedRowKeys(newSelectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  const onClickHandle = async () => {
    setIsExporting(true);

    api.students
      .exportStudents(selectedRowKeys)
      .then(result => {
        let csvContent =
          "data:text/csv;charset=utf-8," +
          result.map(e => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        saveAs(encodedUri, slugify("students"));
        // window.open(encodedUri);
        setIsExporting(false);
      })
      .catch(err => {
        console.error(err);
        setIsExporting(false);
      });
  };

  const onClearCache = async () => {
    setIsClearingCache(true);
    try {
      const result = await api?.cache?.totalDelete();
      setIsClearingCache(false);
    } catch (err) {
      setIsClearingCache(false);
      console.error({ err });
    }
  }

  // render function
  return (
    <div>
      <Layout className="main-full-content">
        <Helmet>
          <title>{t("layouts.navigation.parameters")}</title>
        </Helmet>
        <Content className="main-content-container">
          <div className="container-wrapped export-tabel">
            <CustomPageHeader
              className="audiences-header"
              title={t("layouts.navigation.export")}
              subTitle={t("labels.students")}
              subTitlePosition="top"
              extraActions={[
                <Button
                  className="ant-btn-submit"
                  type="default"
                  disabled={loading || selectedRowKeys.length == 0}
                  loading={isExporting}
                  onClick={onClickHandle}
                >
                  {t("common.export")} ({selectedRowKeys?.length})
                </Button>,
              ]}
            />
            <CustomTable
              className="audiences-table"
              dataSource={settingsList?.data || []}
              columns={columns}
              loading={loading}
              rowSelection={rowSelection}
            />
          </div>
        </Content>
      </Layout>
    </div>
  );
}

Settings.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadSettings: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  loading: selectSettingsLoading(),
  error: selectSettingsError(),
  settingsList: selectSettingsList(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushStateSettings()),
    loadSettings: id => dispatch(loadSettings(id)),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(Settings);
